import { useSession } from '@/auth/useSession';
import { EditableInfoCard, SubmitProps } from '@/components/editable-info-card';
import { MajorConcernIssues } from '@/components/feedbacks/feedback-issues';
import { FeedbackSubmissionDetails } from '@/components/feedbacks/feedback-submission-details';
import { FeedbacksTable } from '@/components/feedbacks/feedbacks-table';
import { SessionFeedbackForm } from '@/components/feedbacks/session-feedback-form';
import {
  AssessmentFormatValidationSchema,
  AssessmentResultForm,
  buildUpdateAssessmentResultsPayload,
  getAssessmentUISection,
  SessionFeedbackFormSchema,
  sessionFeedbackFormSchema,
  useSessionFeedbackCommonData,
} from '@/components/feedbacks/session-feedback-utils';
import { InfoCard } from '@/components/info-card';
import { InfoKeyValue } from '@/components/info-key-value';
import { InfoPage } from '@/components/info-page';
import { Button } from '@/components/ui/button';
import { CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/hooks/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import {
  createAssessmentResult,
  getAssessments,
  getSessionFeedback,
  getSessionFeedbacks,
  updateSessionFeedback,
} from '@/lib/api-calls';
import {
  booleanFromString,
  booleanToString,
  formatInstant,
  formatTimeRange,
  FrequentTimeZones,
  isAirAdmin,
  isNotNullish,
  nullsToUndefined,
} from '@airreading/common';
import { toSessionFeedbackCompoundId } from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type OnChangeFn, type PaginationState } from '@tanstack/react-table';
import axios from 'axios';
import { SquareCheck } from 'lucide-react';
import { useCallback, useState } from 'react';

interface SessionFeedbackPageProps {
  feedbackCompoundId: {
    coachId: string;
    sessionId: string;
    studentId: string;
  };
  pagination: PaginationState & { onPaginationChange: OnChangeFn<PaginationState> };
  showCurrent?: boolean;
}

export function SessionFeedbackPage({
  feedbackCompoundId,
  pagination: { pageIndex, pageSize, onPaginationChange },
  showCurrent = false,
}: SessionFeedbackPageProps) {
  const { data: sessionFeedback, error } = useSuspenseQuery(getSessionFeedback(feedbackCompoundId));
  const { mutateAsync } = useMutation({
    mutationFn: updateSessionFeedback,
  });

  const {
    data: pastFeedbacksResp,
    isPending: isLoadingPastFeedbacks,
    error: pastFeedbacksError,
  } = useQuery(
    getSessionFeedbacks({
      pageIndex,
      pageSize,
      filters: {
        studentId: feedbackCompoundId.studentId,
      },
    }),
  );

  const { actingUser } = useSession();
  const isAdmin = isAirAdmin(actingUser);
  const timeZone = FrequentTimeZones.UsCentral;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const {
    student,
    session,
    coach,
    studentAttended,
    feedbackContent,
    majorConcerns,
    issues,
    feedbackSubmitted,
    assessmentResults,
    submittedAtInstant,
  } = sessionFeedback;

  const pastFeedbacks = (pastFeedbacksResp?.sessionFeedbacks || []).filter((past) => {
    return toSessionFeedbackCompoundId(past) !== toSessionFeedbackCompoundId(feedbackCompoundId);
  });
  const allFeedbacks = pastFeedbacksResp?.sessionFeedbacks ?? pastFeedbacks;

  const {
    assessmentResultsGrouped,
    assessmentContentsById,
    isDiagnosticResultAllEmpty,
    unFinishedAssessmentSections,
    assessmentResultsDefaultValues,
    hasIncompleteDiagnosticLists,
  } = useSessionFeedbackCommonData(sessionFeedback);

  // if a student already has a level placement (learning path and starting lesson),
  // then we no longer need diagnostic feedback from this session
  const studentHasBeenLeveled = !!student.studentProfile?.learningPathId && !!student.studentProfile?.startingLessonId;
  const diagnosticCompletedFromOtherSession = studentHasBeenLeveled && isDiagnosticResultAllEmpty;

  const handleFeedbackSubmission = (
    { formData, form, disableEditing }: SubmitProps<SessionFeedbackFormSchema>,
    action: 'submit' | 'saveAsDraft',
  ) => {
    const { studentAttended, issues: _issues, lateFeedbackDismissed, feedbackContent, majorConcerns } = formData;
    const hasMajorConcerns = _issues.includes('Strong_behavior_issues');
    const issues = _issues.filter((issue) => {
      if (hasMajorConcerns) {
        return true;
      }

      // if Strong_behavior_issues is not selected, filter out the major concern issues
      return !MajorConcernIssues.includes(issue);
    });

    const assessmentIdsNotFinished = assessmentResultsGrouped
      .filter(({ assessment }) => formData.assessmentNotFinishedUiSections.includes(getAssessmentUISection(assessment)))
      .map(({ assessment }) => assessment.id);

    const { assessmentResultsPayload, skippedDiagnosticResultIds, hasDiagnosticAssessments } =
      buildUpdateAssessmentResultsPayload({
        formData,
        assessmentContentsById,
        assessmentIdsNotFinished,
        isSavingAsDraft: action === 'saveAsDraft',
        assessmentResultsGrouped,
        form,
        diagnosticCompletedFromOtherSession,
      });

    let manualValidationPassed = true;
    form.clearErrors();

    // Only validate if submitting (not saving as draft) and student attended
    if (action === 'submit' && studentAttended === 'true') {
      // Validate assessment results
      for (const { assessmentIndex, results, assessment } of assessmentResultsGrouped) {
        if (assessmentIdsNotFinished.includes(assessment.id)) {
          continue;
        }

        results.forEach(({ assessmentContentId, id }, resultIndex) => {
          const content = assessmentContentsById.get(assessmentContentId);
          if (!content) {
            console.warn(
              `Assessment content with ID ${assessmentContentId} not found in assessment contents by ID map.`,
            );
            return;
          }
          // Skip validation in two cases:
          // 1. The assessment result was skipped due to prior errors
          // 2. The session includes a diagnostic assessment and the student has been leveled. So skipping all GLA + Diagnostic feedbacks
          if (skippedDiagnosticResultIds.includes(id) || (hasDiagnosticAssessments && studentHasBeenLeveled)) {
            return;
          }

          const currentResultData = formData.assessmentResults[assessmentIndex].results[resultIndex];

          // Skip validation for diagnostic word lists that are not filled in when diagnosticIncomplete is checked
          if (content.format === 'diagnostic_word_list' && formData.diagnosticIncomplete) {
            const isNotFilledIn =
              !currentResultData.noErrorsMadeInWordList && currentResultData.incorrectWords.length === 0;
            if (isNotFilledIn) {
              return;
            }
          }
          const validationResults = AssessmentFormatValidationSchema[content.format].safeParse(currentResultData);
          if (!validationResults.success) {
            manualValidationPassed = false;
            validationResults.error.errors.forEach(({ path, message }) => {
              const keyName = path[0] as keyof AssessmentResultForm;
              form.setError(`assessmentResults.${assessmentIndex}.results.${resultIndex}.${keyName}`, {
                type: 'manual',
                message,
              });
            });
          }
        });
      }

      if (!manualValidationPassed) {
        toast({
          title: 'Please fill out the required fields or mark the section as incomplete to submit your feedback.',
          variant: 'destructive',
        });
        return;
      }
    }

    return mutateAsync({
      ...feedbackCompoundId,
      lateFeedbackDismissed,
      feedbackContent,
      majorConcerns,
      issues,
      studentAttended: booleanFromString(studentAttended),
      action,
      // if student didn't attend, don't update assessment results. [] will skip updating any, not erasing existing results
      assessmentResults: studentAttended === 'true' ? assessmentResultsPayload : [],
    })
      .then(() => {
        toast({
          title: action === 'submit' ? 'Feedback submitted successfully' : 'Feedback saved as draft',
        });
        disableEditing();
        queryClient.invalidateQueries(getSessionFeedback(feedbackCompoundId));
        queryClient.invalidateQueries(getSessionFeedbacks());
      })
      .catch(() => {
        toast({
          title: `Failed to ${action === 'submit' ? 'submit' : 'save'} feedback. Please try again or contact Air Reading support`,
        });
      });
  };

  const onSubmit = (props: SubmitProps<SessionFeedbackFormSchema>) => {
    return handleFeedbackSubmission(props, 'submit');
  };

  const [selectedContentId, setSelectedContentId] = useState<string | undefined>(undefined);
  const { data: allAssessments } = useQuery(getAssessments());
  const additionalSelectableAssessments = allAssessments
    ?.map((assessment) => ({
      ...assessment,
      // remove the contents that are already in the feedback
      contents: assessment.contents.filter(
        (content) => !assessmentResults.some((result) => result.assessmentContentId === content.id),
      ),
    }))
    // remove the assessments that have no contents after the above filter
    .map((assessment) => (assessment.contents.length === 0 ? null : assessment))
    .filter(isNotNullish);

  // TODO, enable this when we have spec to create ad-hoc assessment result on session feedback page
  const supportCreatingAdditionalAssessmentResults = false;
  const handleCreateAssessmentResult = useCallback(async () => {
    if (selectedContentId) {
      const selectedContent = allAssessments
        ?.flatMap((assessment) =>
          assessment.contents.map((content) => ({
            ...content,
            assessmentId: assessment.id,
          })),
        )
        .find((content) => content.id === selectedContentId);
      if (selectedContent) {
        try {
          await createAssessmentResult({
            sessionId: feedbackCompoundId.sessionId,
            coachId: feedbackCompoundId.coachId,
            studentId: feedbackCompoundId.studentId,
            assessmentContentId: selectedContent.id,
            assessmentId: selectedContent.assessmentId,
          });
          queryClient.invalidateQueries(getSessionFeedback(feedbackCompoundId));
          setSelectedContentId(undefined);
          toast({
            title: 'Assessment result created successfully',
          });
        } catch (error) {
          console.error('Failed to create assessment result:', error);
          if (axios.isAxiosError(error) && error.response?.status === 409) {
            toast({
              title: 'Assessment result already exists for this assessment content',
            });
          } else {
            toast({
              title: 'Failed to create assessment result. Please try again or contact Air Reading support',
            });
          }
        }
      }
    }
  }, [selectedContentId, allAssessments, feedbackCompoundId, queryClient, toast]);

  const selectedContent = allAssessments
    ?.flatMap((assessment) =>
      assessment.contents.map((content) => ({
        ...content,
        assessmentName: assessment.name,
      })),
    )
    .find((content) => content.id === selectedContentId);

  const sessionDateFormatted = `${formatInstant(session.startInstant, timeZone).formattedDateWithoutYear}, ${formatTimeRange(session.startInstant, session.endInstant, timeZone)}`;

  if (error) {
    return (
      <InfoPage headerText="Feedback">
        <div>
          We are sorry, but we could not fetch your feedback. Please notify Air Reading about this issue or try again
          later.
        </div>
      </InfoPage>
    );
  }

  return (
    <InfoPage
      headerText="Student feedback details"
      breadcrumbs={{
        linkItems: [
          {
            label: 'Sessions',
            link: {
              to: '/sessions',
            },
          },
          {
            label: 'Student feedback',
            link: {
              to: '/sessions/session-feedbacks',
            },
          },
        ],
        currentPage: 'Feedback details',
      }}
    >
      <InfoCard headerText="Session information">
        <>
          <InfoKeyValue label="Student" value={student.fullNameOrEmail} />
          <InfoKeyValue label={`Class time (${timeZone})`} value={sessionDateFormatted} />
          <InfoKeyValue label="Teacher" value={coach.fullNameOrEmail} />
          <InfoKeyValue
            label="Lesson"
            value={
              <Link
                to="/lessons/$lessonId"
                params={{ lessonId: session.lesson.id }}
                key={session.lesson.id}
                className="underline text-blue-500"
              >
                {session.lesson.name}
              </Link>
            }
          />
          <InfoKeyValue label="Recording" value={'TODO'} />
        </>
      </InfoCard>
      <EditableInfoCard
        formSchema={sessionFeedbackFormSchema}
        onSubmit={onSubmit}
        headerText={''}
        defaultValues={nullsToUndefined({
          studentAttended: booleanToString(studentAttended),
          assessmentNotFinishedUiSections: [...unFinishedAssessmentSections],
          diagnosticIncomplete: hasIncompleteDiagnosticLists,
          issues: issues ?? [],
          assessmentResults: assessmentResultsDefaultValues,
          lateFeedbackDismissed: sessionFeedback.lateFeedbackDismissed,
          majorConcerns: sessionFeedback.majorConcerns,
          feedbackContent,
        })}
        secondaryActionSection={() => {
          return submittedAtInstant ? (
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <SquareCheck className="h-3.5 w-3.5" />
              <span>
                Feedback submitted at {formatInstant(submittedAtInstant, timeZone).formattedDateWithoutYearAndTime} (
                {timeZone})
              </span>
            </div>
          ) : null;
        }}
        saveButtonLabel={'Submit feedback'}
        editButtonLabel={'Edit feedback'}
        maxItemsPerRow={1}
        contentClass="flex flex-col gap-2"
      >
        {({ editing, form }) => {
          return (
            <>
              {isAdmin && <FeedbackSubmissionDetails sessionFeedback={sessionFeedback} form={form} editing={editing} />}
              <SessionFeedbackForm
                editing={editing}
                form={form}
                studentAttended={studentAttended}
                issues={issues}
                feedbackContent={feedbackContent}
                majorConcerns={majorConcerns}
                assessmentResults={assessmentResultsGrouped}
                feedbackSubmitted={feedbackSubmitted}
                assessmentContentsById={assessmentContentsById}
                diagnosticCompletedFromOtherSession={diagnosticCompletedFromOtherSession}
              />
              {supportCreatingAdditionalAssessmentResults && (
                <div className="col-span-4">
                  <CardTitle className="text-xl font-bold">Additional assessment feedback</CardTitle>
                  <p>
                    If there were additional assessments done for this class for {student.fullNameOrEmail}, you can
                    choose from the dropdown below to add more assessment feedback forms.
                  </p>
                  <div className="flex items-center gap-4 mt-4">
                    <Select value={selectedContentId} onValueChange={(value) => setSelectedContentId(value)}>
                      <SelectTrigger>
                        {selectedContent
                          ? `${selectedContent.assessmentName} - ${selectedContent.name}`
                          : 'Select additional assessment'}
                      </SelectTrigger>
                      <SelectContent>
                        {additionalSelectableAssessments?.map((assessment) =>
                          assessment.contents.map((content) => (
                            <SelectItem key={content.id} value={content.id}>
                              {assessment.name} - {content.name}
                            </SelectItem>
                          )),
                        )}
                      </SelectContent>
                    </Select>
                    <Button type="button" onClick={handleCreateAssessmentResult}>
                      Submit
                    </Button>
                  </div>
                </div>
              )}
            </>
          );
        }}
      </EditableInfoCard>
      <InfoCard headerText={`${student.fullNameOrEmail}'s previous feedback`} isLoading={isLoadingPastFeedbacks}>
        <>
          {pastFeedbacksError ? (
            <div>
              There was an error fetching previous feedback entries. Please try again or contact Air Reading support.
            </div>
          ) : (
            <FeedbacksTable
              sessionFeedbacks={showCurrent ? allFeedbacks : pastFeedbacks}
              emptyState={'No past feedback found for this student'}
              from="session"
              tableHookProps={{
                state: {
                  pagination: {
                    pageIndex,
                    pageSize,
                  },
                },
                onPaginationChange,
                rowCount: pastFeedbacksResp?.total || 0,
              }}
              totalRowCount={pastFeedbacksResp?.total || 0}
            />
          )}
        </>
      </InfoCard>
    </InfoPage>
  );
}
